import clsx from 'clsx'
import { nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { PopupPlacement } from '@/common/types'
import { getValidityStateObject } from '@/common/utils'
import { GenericFormElement } from '@/internal/mixin/genericFormElement'
import { t, translate } from '@/localization/localization'

import DapDSOptionList from '../option-list/option-list.component'
import styles from './combobox.scss?inline'

export default class ComboboxBaseElement extends GenericFormElement {
  @query('input') readonly triggerInput: HTMLInputElement
  @query('dap-ds-option-list') readonly optionList: DapDSOptionList
  @property({ reflect: true }) placement: PopupPlacement = 'bottom-start'
  @property({ type: Boolean, reflect: true }) opened = false
  @property() searchMode: 'none' | 'typehead' | 'autocomplete' | 'manual' =
    'autocomplete'
  @property({ type: Boolean }) search = false
  @property() clearButton?: string = 'true'
  @property() placeholder: string | null
  @property({ type: Boolean }) sync?: boolean
  @property({ type: Boolean }) allowManualInput?: boolean = false
  @property({ type: Boolean }) searchForText?: boolean = false
  @property({ type: String }) searchOnValueSet? = 'false'
  @property({ type: Boolean }) loading?: boolean = false
  @property({ type: String }) selectable?: string = 'true'
  @property({ type: Boolean }) noAnimation?: boolean = false
  @property({ type: Boolean }) textComplete?: boolean = false
  @property({ type: Boolean }) openOnEmpty?: boolean = false
  @property() searchButtonAriaLabel?: string
  @property({ type: Boolean }) subtle: boolean

  @state() selectedItem: any
  @state() currentItem: any
  @state() selectedText: string

  static readonly styles = unsafeCSS(styles)

  protected async firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): Promise<void> {
    super.firstUpdated(_changedProperties)

    await this.optionList.updateComplete

    if (this.value) {
      this.selectedItem = this.optionList
        ?.getAllOptions()
        .find(option => option.value === this.value)
      this.selectedText = this.selectedItem?.optionText || ''
    }
  }

  attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ): void {
    if (name === 'value' && !value) {
      this.clearSelection()
    }

    super.attributeChangedCallback(name, _old, value)
  }

  get isAutoComplete() {
    return this.searchMode === 'autocomplete' || this.searchMode === 'manual'
  }

  get isOpenOnEmpty() {
    return (
      this.openOnEmpty ||
      (!this.openOnEmpty && this.optionList?.getAllOptions().length > 0)
    )
  }

  get isOpenOnAutocomplete() {
    return this.isAutoComplete && this.isOpenOnEmpty
  }

  get shouldHideClearIcon() {
    // Check if input is empty (cross-browser compatible)
    const inputValue = this.triggerInput?.value || ''
    const hasPlaceholder = this.placeholder && this.placeholder.trim() !== ''

    // Only apply JavaScript fallback when there's no placeholder
    // When there IS a placeholder, let CSS :placeholder-shown handle it
    if (hasPlaceholder) {
      return false // Let CSS handle it
    }

    // Hide clear icon when input is empty and has no placeholder
    return inputValue === ''
  }

  handleClick = () => {
    if (!this.optionList?.getAllOptions().length && !this.openOnEmpty) {
      this.hideDropDown()
    } else {
      this.opened ? this.hideDropDown() : this.showDropDown()
    }
  }

  private async handleEnterKey(event: KeyboardEvent) {
    event.preventDefault()
    event.stopPropagation()

    if (this.allowManualInput) {
      return this.handleSearchEnter()
    }

    if (this.currentItem && this.opened) {
      return this.handleCurrentItemEnter()
    }

    if (this.search && !this.isAutoComplete) {
      return this.handleSearchEnter()
    }

    return this.handleDefaultEnter()
  }

  private async handleCurrentItemEnter() {
    if (this.currentItem.value === this.selectedItem?.value) {
      this.hideDropDown()
      this.triggerInput.value = this.selectedItem.text
      this.triggerInput.focus()
      return
    }

    if (this.currentItem.value !== this.selectedItem?.value) {
      if (!this.searchForText) {
        await this.handleSelectedItemChange(this.currentItem)
      } else {
        await this.handleTextSearch(this.currentItem.text)
      }
    }
  }

  private async handleSearchEnter() {
    this.emit('dds-search', { search: this.selectedText })
    this.setValidity()
    return this.hideDropDown()
  }

  private async handleDefaultEnter() {
    if (this.opened) {
      return this.hideDropDown()
    } else {
      // Only open if we have results or openOnEmpty is true
      return (
        (this.openOnEmpty || this.optionList?.getAllOptions().length) &&
        this.showDropDown()
      )
    }
  }

  private async handleSelectedItemChange(item: any) {
    this.selectedItem = item
    this.emit('dds-change', this.selectedItem)
    await this.updateComplete
    this.value = this.selectedItem.value
    this.selectedText = this.currentItem.text
    this.triggerInput.value = this.currentItem.text
    this.triggerInput.focus()
    this.setValidity()
  }

  private async handleTextSearch(text: string) {
    this.value = text
    this.selectedText = text
    this.triggerInput.value = text
    this.triggerInput.focus()
    this.emit('dds-search', { search: text })
    this.setValidity()
  }

  private async handleArrowKeys(event: KeyboardEvent) {
    event.preventDefault()

    if (!this.opened) {
      // Only show dropdown if we have results or openOnEmpty is true
      if (this.openOnEmpty || this.optionList?.getAllOptions().length) {
        this.showDropDown()
      } else {
        return // Don't proceed if we can't open dropdown
      }
    }

    await this.updateComplete
    this.optionList.captureFocus(event.key)
  }

  handleKeyDown = async (event: KeyboardEvent) => {
    if (event.key === 'Tab') {
      return
    }

    if (event.key === 'Enter') {
      return this.handleEnterKey(event)
    }

    if (event.key === 'Escape') {
      this.hideDropDown()
      return this.focus()
    }

    if (
      (event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight') &&
      this.isAutoComplete
    ) {
      return this.handleArrowKeys(event)
    }

    await this.updateComplete
    await this.optionList?.updateComplete

    // typing - only show dropdown if we have results or openOnEmpty is true
    if (this.openOnEmpty || this.optionList?.getAllOptions().length) {
      this.showDropDown()
    }
  }

  handleInput = async (event: InputEvent) => {
    const input = this.triggerInput
    if (
      event.inputType !== 'deleteContentBackward' ||
      (event.inputType === 'deleteContentBackward' && this.allowManualInput)
    ) {
      this.selectedText = input.value
      if (this.search) {
        this.value = input.value
      }
    } else if (this.search) {
      this.selectedText = input.value
      this.value = input.value
    } else if (
      input.value === '' &&
      event.inputType === 'deleteContentBackward'
    ) {
      this.clearSelection()
    }

    this.emit('dds-input', { input: input.value, originalEvent: event })
    this.setValidity()

    // Trigger re-render to update clear icon visibility
    this.requestUpdate()

    // Wait for option list to update with filtered results
    await this.updateComplete
    await this.optionList?.updateComplete

    // Hide dropdown if no results and openOnEmpty is false
    if (!this.openOnEmpty && !this.optionList?.getAllOptions().length) {
      this.hideDropDown()
    }
  }

  async clearSelection() {
    this.triggerInput.value = ''
    this.selectedText = ''
    this.selectedItem = null

    this.value = undefined

    // Trigger re-render to update clear icon visibility
    this.requestUpdate()

    await this.updateComplete

    this.emit('dds-change', this.selectedItem)
  }

  handleListKeyDown = (event: CustomEvent) => {
    if (event.detail.key === 'Escape') {
      this.hideDropDown()
      this.currentItem = this.selectedItem
      this.selectedText = ''
      this.triggerInput.value = this.selectedItem.text
      this.triggerInput.focus()
    }
  }

  handleDocumentMouseDown = (event: MouseEvent) => {
    const path = event.composedPath()
    if (this && !path.includes(this)) {
      this.hideDropDown()
    }
  }

  handleFocus = () => {
    this.emit('dds-focus')
  }

  handleBlur = () => {
    this.emit('dds-blur')
  }

  async showDropDown() {
    if (this.disabled || this.readonly || !this.isAutoComplete || this.opened) {
      return
    }

    // Check if we should open when there are no results
    if (!this.openOnEmpty && !this.optionList?.getAllOptions().length) {
      return
    }

    this.opened = true

    document.addEventListener('mousedown', this.handleDocumentMouseDown)
  }

  async hideDropDown() {
    if (!this.opened) {
      return
    }

    this.opened = false

    document.removeEventListener('mousedown', this.handleDocumentMouseDown)

    await this.updateComplete
    this.focus()
    this.clearSelectionRange()
  }

  public get focusElement() {
    return this.triggerInput
  }

  handleSelectedChange = async (event: CustomEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (this.searchForText) {
      this.selectedText = event.detail.text
      this.value = event.detail.text
      this.triggerInput.value = event.detail.text

      this.emit('dds-search', { search: this.selectedText })

      // Trigger re-render to update clear icon visibility
      this.requestUpdate()

      this.setValidity()
    } else {
      this.selectedItem = event.detail
      this.selectedText = event.detail.text

      if (this.value !== this.selectedItem?.value) {
        this.triggerInput.value = event.detail.text
        this.value = this.selectedItem.value
        this.emit('dds-change', this.selectedItem)

        // Trigger re-render to update clear icon visibility
        this.requestUpdate()

        await this.updateComplete

        if (this.currentItem) {
          this.triggerInput.value = this.currentItem.text
        }
        this.triggerInput.focus()
        this.setValidity()
      }
    }

    this.hideDropDown()
  }

  handleCurrentOptionChange = (event: CustomEvent) => {
    this.currentItem = event.detail

    if (event.detail) {
      if (
        this.textComplete &&
        this.searchMode !== 'manual' &&
        this.searchMode !== 'none'
      ) {
        this.triggerInput.value = event.detail.text
      }

      if (
        this.isAutoComplete &&
        this.selectedText &&
        this.selectedText !== '' &&
        !this.allowManualInput &&
        this.textComplete
      ) {
        this.triggerInput.value = event.detail.text
        this.setSelectionRange(
          this.selectedText.length,
          this.triggerInput.value.length,
        )
      }
    }
  }

  handleListChanged = (event: CustomEvent) => {
    if (!event.detail.elements.length && !this.openOnEmpty) {
      this.hideDropDown()
    }
  }

  handleClearClick = () => {
    if (!this.disabled) {
      this.clearSelection()

      this.triggerInput.focus()
      this.setValidity()

      if (!this.openOnEmpty) {
        this.hideDropDown()
      }

      this.emit('dds-clear')
    }
  }

  handleSearchClick = (event: MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()

    this.setValidity()
    this.emit('dds-search', { search: this.selectedText })
  }

  private setSelectionRange(start: number, end: number) {
    this.triggerInput.setSelectionRange(start, end)
  }

  private clearSelectionRange() {
    const controlValueLength = this.triggerInput.value.length

    this.setSelectionRange(controlValueLength, controlValueLength)
  }

  getValidity() {
    if (this.isAutoComplete) {
      return this.getAutoCompleteValidity()
    }
    return this.getBasicValidity()
  }

  formResetCallback() {
    this.internals.setValidity({})
    this.internals.setFormValue(this.defaultValue)
    this.value = this.defaultValue
    this.selectedItem = this.defaultValue
  }

  private getAutoCompleteValidity(): boolean {
    if (this.allowManualInput) {
      return this.getManualInputValidity()
    }
    if (this.searchForText) {
      return this.getSearchTextValidity()
    }
    return this.getSelectedItemValidity()
  }

  private getManualInputValidity(): boolean {
    return (
      this.triggerInput.value !== '' ||
      (this.selectedItem !== null && this.selectedItem !== undefined)
    )
  }

  private getSearchTextValidity(): boolean {
    return this.triggerInput.value !== ''
  }

  private getSelectedItemValidity(): boolean {
    return !!this.selectedItem
  }

  private getBasicValidity(): boolean {
    return this.triggerInput.value !== ''
  }

  // custom validation
  get validity(): ValidityState {
    if (this.internals.validity) {
      return this.internals.validity
    } else {
      return getValidityStateObject({
        valueMissing: this.required && !this.getValidity(),
      })
    }
  }

  get validationMessage(): string {
    return t('form.validationMessage.select')
  }

  checkValidity() {
    return this.required ? this.getValidity() : true
  }

  reportValidity() {
    return this.required ? this.checkValidity() : true
  }

  setValidity() {
    if (!this.checkValidity()) {
      this.internals.setValidity(
        {
          valueMissing: this.required && !this.getValidity(),
        },
        this.validationMessage,
        this.triggerInput,
      )
    } else {
      this.internals.setValidity({})
    }
  }

  handleInvalid(event: Event) {
    event.preventDefault()
  }

  getActiveDescendant() {
    if (!this.opened) {
      return this.triggerInput
    }

    const activeOption = this.optionList?.getActiveOption()
    return activeOption
  }

  getSearchIconSize(size: string) {
    switch (size) {
      case 'sm':
        return 'md'
      case 'lg':
        return 'lg'
      case 'xs':
        return 'sm'
      default:
        return 'md'
    }
  }

  getInnerIconSize(size: string) {
    switch (size) {
      case 'sm':
        return 16
      case 'lg':
        return 16
      case 'xs':
        return 10
      default:
        return 16
    }
  }

  private renderInput() {
    return html`<input
      role="combobox"
      class="${clsx('input', `input--${this.size}`, {
        'input--disabled': this.disabled,
      })}"
      aria-expanded=${this.opened}
      aria-haspopup="listbox"
      aria-labelledby="label"
      aria-describedby="description ${this.feedback ? 'feedback' : ''}"
      aria-disabled=${this.disabled}
      aria-autocomplete="both"
      aria-controls="option-list"
      aria-activedescendant=${ifDefined(this.getActiveDescendant()?.id)}
      ?required=${this.required}
      ?readonly=${this.readonly}
      ?autofocus=${this.autofocus}
      autocomplete=${this.autocomplete as any}
      ?disabled=${this.disabled}
      value=${this.selectedText !== ''
        ? this.selectedText
        : this.selectedItem?.text || ''}
      placeholder=${ifDefined(this.placeholder ?? '')}
      type=${this.search ? 'search' : 'text'}
      @blur=${this.handleBlur}
      @focus=${this.handleFocus}
      @input=${this.handleInput}
      @keydown=${this.handleKeyDown}
      @click=${this.handleClick} />`
  }

  private renderClearButton() {
    if (this.clearButton !== 'true') return nothing

    return html`<div
      class="${clsx('clear-icon', {
        'clear-icon--hidden': this.shouldHideClearIcon,
      })}">
      <dap-ds-icon-button
        icon="close-line"
        aria-label=${translate('clear')}
        ?disabled=${this.disabled}
        @click=${this.handleClearClick}></dap-ds-icon-button>
    </div>`
  }

  private renderIndicatorIcon() {
    const isAutocompleteOrManual =
      this.searchMode === 'autocomplete' || this.searchMode === 'manual'
    const showLoadingSpinner = this.loading
      ? html`<dap-ds-icon-loading-spinner></dap-ds-icon-loading-spinner>`
      : null

    if (isAutocompleteOrManual && !this.search) {
      return html`<div
        class="${clsx('indicator-icon', {
          'indicator-icon--loading': this.loading,
        })}">
        ${showLoadingSpinner ||
        html`<dap-ds-icon-arrow-right-s-line
          aria-hidden="true"
          @click=${this.handleClick}>
        </dap-ds-icon-arrow-right-s-line>`}
      </div>`
    }

    if (this.search) {
      return html`<div
        class="${clsx('indicator-icon', {
          'indicator-icon--loading': this.loading,
        })}">
        ${showLoadingSpinner ||
        html`<dap-ds-icon-button
          size=${this.getSearchIconSize(this.size)}
          variant=${this.subtle ? 'neutral' : 'inverted-brand'}
          icon="search-line"
          aria-label=${this.searchButtonAriaLabel ?? translate('search')}
          @click=${this.handleSearchClick}>
        </dap-ds-icon-button>`}
      </div>`
    }

    return nothing
  }

  private renderSelectActions() {
    return html`<div
      class="${clsx('select-action', `select-action--${this.size}`, {
        'select-action--disabled': this.disabled,
        'select-action--readonly': this.readonly,
        'select-action--opened': this.opened,
      })}">
      ${this.renderClearButton()} ${this.renderIndicatorIcon()}
    </div>`
  }

  private renderOptionList() {
    return html`<dap-ds-option-list
      class="option-list"
      part="option-list"
      id="option-list"
      .search=${this.search}
      searchMode=${this.allowManualInput ? 'none' : this.searchMode}
      searchOnValueSet=${this.searchOnValueSet === 'true'}
      filterText=${this.selectedText}
      size=${ifDefined(this.size)}
      value=${ifDefined(this.value)}
      ?selectable=${this.selectable === 'true'}
      @dds-option-change=${this.handleSelectedChange}
      @dds-keydown=${this.handleListKeyDown}
      @dds-current-change=${this.handleCurrentOptionChange}
      @dds-list-changed=${this.handleListChanged}>
      <slot></slot>
    </dap-ds-option-list>`
  }

  renderCombobox() {
    return html` ${(this, this.renderLabel())}
      <div>
        <dap-ds-popup
          part="base"
          class="select-popup"
          overflow
          ?opened=${this.opened}
          ?hasArrow=${false}
          placement=${this.placement}
          floatingStrategy="fixed"
          offset="4"
          ?sync=${this.sync}
          @dds-closed=${this.hideDropDown}
          @dds-opened=${this.showDropDown}>
          <div
            slot="trigger"
            part="trigger"
            class="${clsx('select', `select--${this.size}`, {
              'select--disabled': this.disabled,
              'select--readonly': this.readonly,
              'select--opened': this.opened,
              'select--loading': this.loading,
              'select--success': this.status === 'success',
              'select--error': this.status === 'error',
              'select--no-animation': this.noAnimation,
            })}">
            ${this.renderInput()} ${this.renderSelectActions()}
          </div>
          ${this.renderOptionList()}
        </dap-ds-popup>
        ${this.feedback &&
        html`<dap-ds-feedback
          part="feedback"
          feedbackType=${ifDefined(this.feedbackType)}
          feedbackSize="${ifDefined(this.size as 'lg' | 'sm' | 'xs')}"
          id="feedback">
          ${this.feedback}
        </dap-ds-feedback>`}
      </div>`
  }
}
